import { getEffectiveScanValue, type ProgressUpdate, type ScanTemplateValues, type ScanValue } from '@kaiizen/kai-scan-template-system';

import {
  getModifierChanges,
  type NamedModifier,
  type PresentableLineItem,
  presentCodeNameAsWords,
  presentLineItemProduct,
  presentScanValueLabel,
  type ScanPartFieldChange,
  scanValueByKeyPresenter,
} from '@kaiizen/kai-presenters';
import type { ScanPartHistoryRecord } from '@kaiizen/kai-pro-api';

import { WORK_PROGRESS_STATUS_LABEL } from './taskWorkProgressActivityLib';

/** What presenting a version's fields needs beyond the versions themselves. */
export type ScanPartFieldChangeContext = {
  /** The part's value templates, to present its values in their units; values present raw without them. */
  valueTemplates?: ScanTemplateValues;
  contractorNamesById: ReadonlyMap<string, string>;
  /** The org's modifiers, known rather than enabled: a disabled modifier's past edits still read by its name. */
  knownModifiers?: readonly NamedModifier[];
};

type PartData = Record<string, unknown>;

/** A change that set or cleared a modifier: a change-order trigger. */
export const isModifierChange = ({ isModifier }: ScanPartFieldChange) => !!isModifier;

/** Bookkeeping fields that change without the part changing in any way a person would call an edit. */
const IGNORED_FIELDS = new Set(['createdAt', 'updatedAt', 'agentUpdatedAt', 'agentUpdateAcceptedAt', 'skuId']);

/** Fields presented by their own rules rather than one label and value each. */
const SEPARATELY_PRESENTED_FIELDS = new Set(['values', 'tags']);

const isSame = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

const presentPlain = (value: unknown): string | undefined => {
  if (value == null || value === '') return undefined;
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'string' || typeof value === 'number') return String(value);
  if (Array.isArray(value)) return `${value.length}`;
  return undefined;
};

const getLatestWorkStatus = (progressUpdates: unknown) => {
  const status = (progressUpdates as ProgressUpdate[] | undefined)?.findLast(update => update.status)?.status;
  return status && WORK_PROGRESS_STATUS_LABEL[status];
};

/** How each known field reads: its label and how its value presents. Unknown fields read by their key in words, presented plainly. */
const getFieldPresenters = (
  { contractorNamesById }: ScanPartFieldChangeContext,
): Record<string, { label: string; present: (value: unknown) => string | undefined }> => ({
  assignedContractorId: {
    label: 'Contractor',
    present: value => value === null ? 'Default' : typeof value === 'string' ? contractorNamesById.get(value) ?? 'Unknown contractor' : undefined,
  },
  customLabel: { label: 'Label', present: presentPlain },
  notApplicable: { label: 'Not applicable', present: presentPlain },
  skuDetails: { label: 'Product', present: value => presentLineItemProduct({ skuDetails: value as PresentableLineItem['skuDetails'] }) },
  photos: { label: 'Photos', present: presentPlain },
  videos: { label: 'Videos', present: presentPlain },
  audio: { label: 'Audio', present: presentPlain },
  progressUpdates: { label: 'Work status', present: getLatestWorkStatus },
  trade: { label: 'Trade', present: presentPlain },
  userAdded: { label: 'Added by user', present: presentPlain },
});

const getValueChanges = (
  previousValues: Record<string, ScanValue> = {},
  currentValues: Record<string, ScanValue> = {},
  valueTemplates?: ScanTemplateValues,
) => {
  const presentValue = (key: string, value: ScanValue | undefined) => {
    const effectiveValue = getEffectiveScanValue(value);
    if (effectiveValue == null) return undefined;
    return valueTemplates?.[key] ? scanValueByKeyPresenter(key, effectiveValue, valueTemplates) || undefined : presentPlain(effectiveValue);
  };
  return [...new Set([...Object.keys(previousValues), ...Object.keys(currentValues)])]
    .map(key => ({
      field: presentScanValueLabel(key, valueTemplates),
      before: presentValue(key, previousValues[key]),
      after: presentValue(key, currentValues[key]),
    }))
    .filter(({ before, after }) => before !== after);
};

/**
 * Every field that differs between a scan part's previous version and the given one, presented for
 * people: each value by its label (presentScanValueLabel) in its template's units, the contractor by name. The child list is not a field:
 * an added or removed child has its own entry.
 * With no previous version (the part was just added), the fields it was added with.
 */
export const getScanPartFieldChanges = (
  previous: ScanPartHistoryRecord | undefined,
  current: ScanPartHistoryRecord,
  context: ScanPartFieldChangeContext,
): ScanPartFieldChange[] => {
  const previousData = (previous?.data ?? {}) as PartData;
  const currentData = (current.data ?? {}) as PartData;
  const fieldPresenters = getFieldPresenters(context);

  const fieldChanges = [...new Set([...Object.keys(previousData), ...Object.keys(currentData)])]
    .filter(key => !SEPARATELY_PRESENTED_FIELDS.has(key) && !IGNORED_FIELDS.has(key) && !isSame(previousData[key], currentData[key]))
    .map(key => {
      const { label, present } = fieldPresenters[key] ?? { label: presentCodeNameAsWords(key), present: presentPlain };
      return { field: label, before: present(previousData[key]), after: present(currentData[key]) };
    });

  return [
    ...getValueChanges(previousData.values as Record<string, ScanValue>, currentData.values as Record<string, ScanValue>, context.valueTemplates),
    ...fieldChanges,
    ...getModifierChanges(previousData.tags as PartData | undefined, currentData.tags as PartData | undefined, context.knownModifiers ?? []),
  ].filter(({ before, after }) => previous || after !== undefined);
};
